import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ExcelExportButton from "./DeliveryAnalysis-Excel";

type TableType =
  | "ASSIGNED"
  | "DELIVERED"
  | "UNDELIVERED"
  | "DISTRIBUTION"
  | "CANCELLED"
  | null;

interface DeliveryAnalysisDatatableProps {
  selectedTable: TableType;
  data: any[];
}

const DeliveryAnalysisDatatable: React.FC<DeliveryAnalysisDatatableProps> = ({
  selectedTable,
  data,
}) => {
  const formatDate = (date: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const commonTableProps = {
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    stripedRows: true,
    showGridlines: true,
    sortMode: "multiple" as const,
    paginatorTemplate:
      "RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink",
    currentPageReportTemplate: "{first} to {last} of {totalRecords}",
    responsiveLayout: "scroll" as const,
    className: "p-datatable-sm",
  };

  return (
    <div className="col-12 p-0">
      <div className="mb-3">
        <ExcelExportButton
          data={data}
          fileName={`${selectedTable}_Orders_${new Date().toLocaleDateString()}`}
          tableType={selectedTable}
        />
      </div>

      {/* ASSIGNED TABLE */}
      {selectedTable === "ASSIGNED" && (
        <DataTable
          value={data}
          {...commonTableProps}
          emptyMessage="No assigned orders found."
        >
          <Column
            field="orderNo"
            header="Order #"
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="assignedDate"
            header="Assigned Date"
            sortable
            body={(row) => formatDate(row.assignedDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="assignedTo"
            header="Assigned To"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerName"
            header="Customer Name"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerNumber"
            header="Customer Number"
            sortable
            style={{ minWidth: "140px" }}
          />
        </DataTable>
      )}

      {/* DELIVERED TABLE */}
      {selectedTable === "DELIVERED" && (
        <DataTable
          value={data}
          {...commonTableProps}
          emptyMessage="No delivered orders found."
        >
          <Column
            field="orderNo"
            header="Order #"
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="assignedDate"
            header="Assigned Date"
            sortable
            body={(row) => formatDate(row.assignedDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="deliveredDate"
            header="Delivered Date"
            sortable
            body={(row) => formatDate(row.deliveredDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="deliveredBy"
            header="Delivered By"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerName"
            header="Customer Name"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerNumber"
            header="Customer Number"
            sortable
            style={{ minWidth: "140px" }}
          />
        </DataTable>
      )}

      {/* UNDELIVERED TABLE */}
      {selectedTable === "UNDELIVERED" && (
        <DataTable
          value={data}
          {...commonTableProps}
          emptyMessage="No undelivered orders found."
        >
          <Column
            field="orderNo"
            header="Order #"
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="assignedDate"
            header="Assigned Date"
            sortable
            body={(row) => formatDate(row.assignedDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="assignedTo"
            header="Assigned To"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerName"
            header="Customer Name"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerNumber"
            header="Customer Number"
            sortable
            style={{ minWidth: "140px" }}
          />
          <Column
            field="delayedReason"
            header="Delayed Reason"
            sortable
            style={{ minWidth: "200px" }}
          />
        </DataTable>
      )}

      {/* DISTRIBUTION TABLE */}
      {selectedTable === "DISTRIBUTION" && (
        <DataTable
          value={data}
          {...commonTableProps}
          emptyMessage="No orders found for this delivery time."
        >
          <Column
            field="orderNo"
            header="Order #"
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="createdDate"
            header="Created Date"
            sortable
            body={(row) => formatDate(row.createdDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="deliveredDate"
            header="Delivered Date"
            sortable
            body={(row) => formatDate(row.deliveredDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="driverName"
            header="Delivered By"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerName"
            header="Customer Name"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerNumber"
            header="Customer Number"
            sortable
            style={{ minWidth: "140px" }}
          />
        </DataTable>
      )}

      {/* CANCELLED TABLE */}
      {selectedTable === "CANCELLED" && (
        <DataTable
          value={data}
          {...commonTableProps}
          emptyMessage="No cancelled orders found."
        >
          <Column
            field="orderNo"
            header="Order #"
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="assignedDate"
            header="Assigned Date"
            sortable
            body={(row) => formatDate(row.assignedDate)}
            style={{ minWidth: "140px" }}
          />
          <Column
            field="assignedTo"
            header="Assigned To"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerName"
            header="Customer Name"
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="customerNumber"
            header="Customer Number"
            sortable
            style={{ minWidth: "140px" }}
          />
          <Column
            field="delayedReason"
            header="Delay Reason"
            sortable
            style={{ minWidth: "200px" }}
          />
        </DataTable>
      )}
    </div>
  );
};

export default DeliveryAnalysisDatatable;
